import { AdvancedSearchURLParams } from '@ltvco/refresh-lib/v2';

import { PlaceholderItem } from 'components/DeepSearch/types/chatTypes';
import {
  GenericReportResponse,
  ProcessedReportData,
} from 'components/DeepSearch/types/reportTypes';
import {
  processPropertyReportForOverviewAndOwners,
  processEmailReportForOverviewAndOwners,
  processPhoneReportForOverviewAndOwners,
  processVehicleReportForOverviewAndOwners,
  processPersonReportForOverviewAndOwners,
  processContactReportForOverviewAndOwners,
  processAncestorSearchReportForOverviewAndOwners,
  processObituaryReportForOverviewAndOwners,
  processUsernameReportForOverviewAndOwners,
} from 'components/DeepSearch/utils/reportUtils';

export const MAX_VISIBLE_RELATIVES = 2;

/**
 * DeepSearch's chat surface (sidebar + layout) intentionally switches to its
 * mobile layout at 1024px — a deliberate divergence from the dashboard's MUI
 * `lg` (1200px). Don't "align" it to `lg` without also moving the layout, or
 * the two desync in the 1024–1200px band.
 */
export const DEEPSEARCH_MOBILE_MAX_WIDTH = 1024;
export const DEEPSEARCH_MOBILE_MEDIA_QUERY = `(max-width: ${DEEPSEARCH_MOBILE_MAX_WIDTH}px)`;
export const MAX_VISIBLE_ALIASES = 3;

export const DEEPSEARCH_BASE_PATH = '/deepsearch';
export const DEEPSEARCH_ROUTES = {
  chat: `${DEEPSEARCH_BASE_PATH}/chat`,
  chatConversation: `${DEEPSEARCH_BASE_PATH}/chat/conversation`,
  library: `${DEEPSEARCH_BASE_PATH}/chat/library`,
  account: `${DEEPSEARCH_BASE_PATH}/account`,
  upgradePlan: `${DEEPSEARCH_BASE_PATH}/upgrade/plan`,
};

export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// ---- relocated from shared/chat ----
// Width of the side column (report / filters / monitoring) shared by the chat
// and DeepSearch layouts.
export const CHAT_COLUMN_WIDTH = '500px';

// Services constants
export const SEARCH_TYPES = {
  PHONE: 'phone',
  EMAIL: 'email',
  PROPERTY: 'property',
  VEHICLE: 'vehicle',
  OBITUARY: 'obituary',
  PERSON: 'person',
  USERNAME: 'username',
  CONTACT: 'contact',
  ANCESTOR: 'ancestor',
  COURTCASES: 'courtcases',
  COURT_OPINIONS: 'courtopinions',
};

export const FEEDBACK_FORM_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSeQsaoj6vcWXFO1pdupPV10I0cE5O13A9qgdLqeI4MAnEqt9g/viewform';

export const PLACEHOLDER_COLLECTION: PlaceholderItem[] = [
  {
    searchType: SEARCH_TYPES.PERSON,
    chipTitle: 'Find a person',
    suggestionTitle: 'Can you help me search for this person?',
    chipMainText: 'Help me find ',
    chipExampleText: 'full name in city, state',
    suggestionMainText: 'Can you help me search for ',
    suggestionExampleText: 'John Smith in New York, NY?',
  },
  {
    searchType: SEARCH_TYPES.PHONE,
    chipTitle: 'Search a phone',
    suggestionTitle: 'Is this phone number spam?',
    chipMainText: 'Search ',
    chipExampleText: 'phone number',
    suggestionMainText: 'Is this phone number spam? ',
    suggestionExampleText: '555-123-4567',
  },
  {
    searchType: SEARCH_TYPES.PROPERTY,
    chipTitle: 'View address info',
    suggestionTitle: 'Who owns this address?',
    chipMainText: 'View address info for ',
    chipExampleText: 'full address',
    suggestionMainText: 'Who owns ',
    suggestionExampleText: '123 Main St. Los Angeles, CA?',
  },
  {
    searchType: SEARCH_TYPES.EMAIL,
    chipTitle: 'Check an email',
    suggestionTitle: undefined,
    chipMainText: 'Check ',
    chipExampleText: 'email',
    suggestionMainText: 'Check ',
    suggestionExampleText: 'email',
  },
  {
    searchType: SEARCH_TYPES.VEHICLE,
    chipTitle: 'Search a vehicle',
    suggestionTitle: 'Any crashes on this car?',
    chipMainText: 'Search ',
    chipExampleText: 'VIN or license plate + state',
    suggestionMainText: 'Any crashes on ',
    suggestionExampleText: 'VIN or license plate + state?',
  },
];

// UI constants
export const HELP_MODAL_ANIMATIONS = [
  {
    src: 'https://lottie.host/9506ef34-53d8-4dca-8275-18819dad4c45/AYJ3KDct6D.lottie',
    duration: 3750, // Estimated duration in milliseconds
  },
  {
    src: 'https://lottie.host/ad3210be-0b0d-46eb-96dc-af7bc863f2fa/A1L1qve9LY.lottie',
    duration: 13000, // Estimated duration in milliseconds
  },
];

export const ROLES = {
  USER: 'user' as const,
  ASSISTANT: 'assistant' as const,
};

export const RESPONSE_TYPE = {
  RESPONSE: 'response',
  ERROR: 'error',
};

export const NULL_SEARCH_PARAMS: AdvancedSearchURLParams = {
  fullName: '',
  fName: '',
  lName: '',
  title: '',
  company: '',
  level: [],
  industry: '',
  company_size_min: '',
  company_size_max: '',
  birthMonth: '',
  birthYear: '',
  deathMonth: '',
  deathYear: '',
  address: '',
  mName: '',
  city: '',
  state: '',
  age: '',
  isDeceased: false,
  location: '',
  country: '',
  eventType: '',
  eventMonth: '',
  eventYear: '',
  advancedSearch: false,
  source: [],
};

export const DEEPSEARCH_REPORT_TYPES_MAP = {
  [SEARCH_TYPES.PERSON]: 'detailed_person_report',
  [SEARCH_TYPES.PHONE]: 'reverse_phone_report',
  [SEARCH_TYPES.PROPERTY]: 'property_report',
  [SEARCH_TYPES.EMAIL]: 'email_report',
  [SEARCH_TYPES.VEHICLE]: 'vehicle_report',
  [SEARCH_TYPES.OBITUARY]: 'obituary_report',
  [SEARCH_TYPES.ANCESTOR]: 'ancestry_report',
  [SEARCH_TYPES.CONTACT]: 'contact_report',
  [SEARCH_TYPES.COURTCASES]: 'courtcases_report',
  [SEARCH_TYPES.USERNAME]: 'username_report',
  [SEARCH_TYPES.COURT_OPINIONS]: 'courtopinions_report',
};

export const REPORT_PROCESSORS = {
  [SEARCH_TYPES.PHONE]: (rawData: GenericReportResponse, permalink: string) =>
    processPhoneReportForOverviewAndOwners(rawData, permalink),
  [SEARCH_TYPES.EMAIL]: (rawData: GenericReportResponse, permalink: string) =>
    processEmailReportForOverviewAndOwners(rawData, permalink),
  [SEARCH_TYPES.PROPERTY]: (
    rawData: GenericReportResponse,
    permalink: string
  ) => processPropertyReportForOverviewAndOwners(rawData, permalink),
  [SEARCH_TYPES.VEHICLE]: (rawData: GenericReportResponse, permalink: string) =>
    processVehicleReportForOverviewAndOwners(rawData, permalink),
  [SEARCH_TYPES.PERSON]: (rawData: GenericReportResponse, permalink: string) =>
    processPersonReportForOverviewAndOwners(rawData, permalink),
  [SEARCH_TYPES.CONTACT]: (rawData: GenericReportResponse, permalink: string) =>
    processContactReportForOverviewAndOwners(rawData, permalink),
  [SEARCH_TYPES.ANCESTOR]: (
    rawData: GenericReportResponse,
    permalink: string
  ) => processAncestorSearchReportForOverviewAndOwners(rawData, permalink),
  [SEARCH_TYPES.OBITUARY]: (
    rawData: GenericReportResponse,
    permalink: string
  ) => processObituaryReportForOverviewAndOwners(rawData, permalink),
  [SEARCH_TYPES.USERNAME]: (
    rawData: GenericReportResponse,
    permalink: string
  ) => processUsernameReportForOverviewAndOwners(rawData, permalink),
} as {
  // eslint-disable-next-line no-unused-vars
  [key in keyof typeof SEARCH_TYPES]: (
    // eslint-disable-next-line no-unused-vars
    rawData: GenericReportResponse,
    // eslint-disable-next-line no-unused-vars
    permalink: string
  ) => ProcessedReportData;
};

export const AVM_PARTNERS_ARRAY = ['bk', 'ca', 'arch'];
